// src/components/SearchBar.jsx

import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import debounce from "lodash.debounce";
import moment from "moment";
import { Autocomplete, TextField, Box, AppBar, Container, Dialog, DialogTitle, DialogContent, IconButton, Button, Typography, Grid, useTheme, useMediaQuery, styled } from "@mui/material";
import { Search, Calendar, SlidersHorizontal } from "lucide-react";
import SearchBarCalendar from "./SearchBarCalendar";
import { autocompleteListings } from "../services/api";

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    position: "static",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "none",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(1),
}));

const SearchContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0px 2px 2px rgba(0,0,0,0.04)",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
    height: 52,
    width: "100%",
}));

const SearchIconWrapper = styled(Box)(({ theme }) => ({
    marginRight: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    color: theme.palette.text.primary,
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
    flexGrow: 1,
    "& .MuiInputBase-root": {
        height: 52,
        fontSize: 20,
        fontFamily: theme.typography.fontFamily,
    },
    "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
    },
}));

const IconButtonWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(1),
    gap: theme.spacing(1),
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    padding: 8,
    color: theme.palette.text.primary,
}));

const DateDisplay = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    textAlign: "center",
}));

const DateLabel = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    fontSize: 12,
    color: theme.palette.text.secondary,
}));

const DateValue = styled(Typography)(({ theme }) => ({
    fontSize: 14,
    color: theme.palette.text.primary,
}));

const DoneButton = styled(Button)(({ theme }) => ({
    borderRadius: 24,
    padding: "8px 24px",
    backgroundColor: "#34A853",
    color: "#fff",
    "&:hover": { backgroundColor: "#2D9147" },
}));

const formatToDateString = (date) => {
    if (!date) return "";
    return moment(date).format("YYYY-MM-DD");
};
const parseToDateObject = (str) => {
    if (!str) return null;
    return new Date(str);
};

const SearchBar = ({
    searchInput,
    onSearchInputChange,
    onSearchSubmit,
    onFilterClick,
    initialDateRange,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Autocomplete state
    const [suggestions, setSuggestions] = useState([]);
    const fetchAutocomplete = useMemo(
        () =>
            debounce(async (value) => {
                if (!value || value.length < 2) {
                    setSuggestions([]);
                    return;
                }
                try {
                    const res = await autocompleteListings(value);
                    setSuggestions(res.data);
                } catch (err) {
                    console.error("Autocomplete error", err);
                }
            }, 300),
        []
    );
    useEffect(() => () => fetchAutocomplete.cancel(), [fetchAutocomplete]);

    // Date picker state (strings YYYY-MM-DD)
    const [dateRange, setDateRange] = useState(["", ""]);
    const [calendarOpen, setCalendarOpen] = useState(false);
    useEffect(() => {
        if (initialDateRange?.length === 2) {
            setDateRange(initialDateRange.map(d => formatToDateString(d)));
        }
    }, [initialDateRange]);
    const [startDate, endDate] = dateRange;

    const handleDayClick = ({ dateString }) => {
        // new start or end logic
        if (!startDate || (startDate && endDate)) {
            setDateRange([dateString, ""]);
        } else {
            if (moment(dateString).isBefore(moment(startDate))) {
                setDateRange([dateString, ""]);
            } else {
                setDateRange([startDate, dateString]);
            }
        }
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        const dates = dateRange.map(parseToDateObject);
        onSearchSubmit(dates);
    };

    return (
        <>
            <StyledAppBar>
                <Container maxWidth="lg">
                    <form onSubmit={handleSearch} style={{ display: "flex", alignItems: "center" }}>
                        <SearchContainer>
                            <SearchIconWrapper><Search size={20} /></SearchIconWrapper>

                            <StyledAutocomplete
                                freeSolo
                                fullWidth
                                options={suggestions}
                                inputValue={searchInput}
                                onInputChange={(e, val) => {
                                    onSearchInputChange({ target: { value: val } });
                                    fetchAutocomplete(val);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleSearch();
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder={isMobile ? "Search…" : "Borrow your next adventure…"}
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { fontSize: 20 },
                                        }}
                                    />
                                )}
                            />

                            <IconButtonWrapper>
                                <StyledIconButton onClick={() => setCalendarOpen(true)} aria-label="select dates">
                                    <Calendar size={20} />
                                </StyledIconButton>
                                <StyledIconButton onClick={onFilterClick} aria-label="filters">
                                    <SlidersHorizontal size={20} />
                                </StyledIconButton>
                            </IconButtonWrapper>
                        </SearchContainer>
                    </form>
                </Container>
            </StyledAppBar>

            <Dialog open={calendarOpen} onClose={() => setCalendarOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Select dates</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                            <DateDisplay>
                                <DateLabel>Pick Up</DateLabel>
                                <DateValue>{startDate ? moment(startDate).format("MMM D") : "—"}</DateValue>
                            </DateDisplay>
                        </Grid>
                        <Grid item xs={6}>
                            <DateDisplay>
                                <DateLabel>Return</DateLabel>
                                <DateValue>{endDate ? moment(endDate).format("MMM D") : "—"}</DateValue>
                            </DateDisplay>
                        </Grid>
                    </Grid>
                    <Box display={{ xs: "block", md: "flex" }} gap={2}>
                        <Box flex={1}>
                            <SearchBarCalendar
                                onDayPress={handleDayClick}
                                initialDate={moment().format("YYYY-MM-DD")}
                                selectedStartDate={startDate}
                                selectedEndDate={endDate}
                            />
                        </Box>
                        <Box flex={1} display={{ xs: "none", sm: "block" }}>
                            <SearchBarCalendar
                                onDayPress={handleDayClick}
                                initialDate={moment().add(1, "month").format("YYYY-MM-DD")}
                                selectedStartDate={startDate}
                                selectedEndDate={endDate}
                            />
                        </Box>
                    </Box>
                    <Box mt={3} textAlign="right">
                        <DoneButton variant="contained" onClick={() => { handleSearch(); setCalendarOpen(false); }}>
                            Done
                        </DoneButton>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

SearchBar.propTypes = {
    searchInput: PropTypes.string.isRequired,
    onSearchInputChange: PropTypes.func.isRequired,
    onSearchSubmit: PropTypes.func.isRequired,
    onFilterClick: PropTypes.func.isRequired,
    initialDateRange: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
};

export default SearchBar;
